import { useCallback, useRef, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import throttle from "lodash/throttle";
import { Check } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate, useSearchParams } from "react-router";

import { SlugInput } from "@/components/pieces/form/SlugInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateWorkspaceMutation, ValidSlugDocument } from "@/graphql";

const SLUG_INPUT_WAIT_TIME = 500;

interface CreateWorkspaceProps {
  redirectToSignup?: boolean;
  onCreateWorkspaceComplete?: (token?: string) => void;
  onSlugChange?: (slug: string) => void;
  errorText?: string;
  loading?: boolean;
}

export function CreateWorkspaceForm({
  redirectToSignup = false,
  onCreateWorkspaceComplete,
  onSlugChange,
  errorText,
  loading = false,
}: CreateWorkspaceProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [validationResult, setValidationResult] = useState<{
    valid?: boolean;
    errorMessage?: string | null;
  }>({});
  const navigate = useNavigate();

  const [validateSlug] = useLazyQuery(ValidSlugDocument, {
    onCompleted: ({ validSlug }) => {
      setValidationResult(validSlug);
    },
  });

  const checkValidSlugRef = useRef(
    throttle((slug) => {
      if (slug) {
        validateSlug({
          variables: {
            slug,
          },
        });
      }
    }, SLUG_INPUT_WAIT_TIME),
  );

  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("nextPath");

  const getRefCode = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("refcode");
    }
    return "";
  }, []);

  const [createWorkspace, { loading: creating }] = useCreateWorkspaceMutation({
    onCompleted: function (data) {
      const { error } = data.createWorkspace || {};
      if (error) {
        setValidationResult({
          valid: false,
          errorMessage: error.message,
        });
        return;
      }

      const urlSlug = data.createWorkspace?.workspace?.urlSlug;
      const workspaceSignedToken = data.createWorkspace?.workspaceSignedToken;

      if (urlSlug && workspaceSignedToken && redirectToSignup) {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("refcode");
          if (USE_SUBDOMAINS) {
            window.location.href = `/to-workspace/${urlSlug}?path=/signup?asWorkspaceAdmin=true&v=new-org&workspaceSignedToken=${workspaceSignedToken}&nextPath=${nextPath}`;
          } else {
            navigate(
              `/signup?asWorkspaceAdmin=true&v=new-org&workspaceSignedToken=${workspaceSignedToken}&nextPath=${nextPath}&workspace=${urlSlug}`,
            );
          }
        }
      }

      onCreateWorkspaceComplete?.(workspaceSignedToken ?? undefined);
    },
  });

  const handleCreateWorkspace = useCallback(() => {
    createWorkspace({
      variables: {
        name: name,
        slug: slug,
        refCode: getRefCode() || undefined,
      },
    });
  }, [name, slug, getRefCode, createWorkspace]);

  useHotkeys("enter", handleCreateWorkspace, {}, [handleCreateWorkspace]);

  return (
    <div className="space-y-6 p-2">
      <div className="space-y-2">
        <div className="text-md font-semibold">Organization Name</div>
        <Input
          placeholder="Vandelay Industries"
          value={name}
          autoFocus={true}
          onChange={(e) => {
            setName(e.target.value);
          }}
          name="organizationName"
          className="py-6 px-3 text-base"
        />
      </div>

      <div className="space-y-1">
        <div className="text-md font-semibold">Workspace URL</div>
        <div className="text-muted-foreground mt-1 mb-3 text-sm">
          Set your branded workspace
        </div>
        <div className="flex items-center gap-2.5">
          <SlugInput
            placeholder="vandelay"
            name="slug"
            maxLength={50}
            value={slug}
            className="w-48 py-6 px-3 text-base"
            onChange={(value) => {
              setSlug(value);
              checkValidSlugRef.current(value);
              if (onSlugChange) {
                onSlugChange(value);
              }
            }}
          />
          <div className="font-semibold text-lg">.exec.com</div>
        </div>
        <div className="flex my-3 h-4 items-center">
          {slug && validationResult && (
            <>
              {validationResult.valid && (
                <div className="flex items-center">
                  <Check className="size-5 text-green-600 mr-2.5" />
                  <div className="text-muted-foreground">URL available</div>
                </div>
              )}
              {validationResult.errorMessage && (
                <div className="text-red-600">
                  {validationResult.errorMessage}
                </div>
              )}
            </>
          )}
        </div>
        {errorText && <div className="my-3 text-red-600">{errorText}</div>}
      </div>

      <Button
        size="lg"
        type="submit"
        intent="primary"
        disabled={
          loading ||
          creating ||
          !(name && slug && validationResult && validationResult.valid)
        }
        onClick={handleCreateWorkspace}
      >
        Continue
      </Button>
    </div>
  );
}
